import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import SidebarPanel from '../admin/SidebarPanel';
import RetellCallViewer from './RetellCallViewer';
import LeadAndOpportunityTracker from './shared/LeadAndOpportunityTracker';
import CallQueuePanel from './CallQueuePanel';
import CommunicationComposer from '../shared/CommunicationComposer';
import AlertMessage from '../shared/alertMessage';
import { adminApi } from '../../services/api';
import { useIsAnkrAdmin } from '../../context/AuthContext';
import { trackCellEdit as trackLeadCellEdit } from '../redux/slices/sales-module/salesLeadsSlice';
import { trackCellEdit as trackOpportunityCellEdit } from '../redux/slices/sales-module/salesOpportunitySlice';
import { toE164, isValidE164 } from '../../utils/phoneUtils';
import PhoneNumberManager from './shared/PhoneNumberManager';

const OUTREACH_TYPES = {
  CALL: 'call',
  EMAIL: 'email'
};

/**
 * Sanitize HTML email body to prevent tracking pixel loads
 * Only removes tracking pixels (1x1 images with tracking URLs)
 * Keeps legitimate images to preserve email appearance
 */
const sanitizeEmailHtml = (html) => {
  if (!html) return '';

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Remove tracking pixels specifically (1x1 images or images with tracking URLs)
  const images = tempDiv.querySelectorAll('img');
  images.forEach(img => {
    const src = img.getAttribute('src') || '';
    const width = img.getAttribute('width');
    const height = img.getAttribute('height');

    // Check if it's a tracking pixel:
    // 1. URL contains tracking pattern: /track/email/
    // 2. OR it's a 1x1 pixel (common tracking pixel size)
    const isTrackingUrl = src.includes('/track/email/') || src.includes('tracking_id=');
    const isOnePixel = (width === '1' || width === 1) && (height === '1' || height === 1);

    if (isTrackingUrl || isOnePixel) {
      // Remove tracking pixel silently (don't show placeholder for cleaner UI)
      img.remove();
    }
  });

  // Remove all scripts for security
  const scripts = tempDiv.querySelectorAll('script');
  scripts.forEach(script => script.remove());

  // Remove iframes for security
  const iframes = tempDiv.querySelectorAll('iframe');
  iframes.forEach(iframe => iframe.remove());

  return tempDiv.innerHTML;
};

/**
 * RetellCallPanel - Side panel for initiating Agentic AI calls and emails
 *
 * @param {Object} props
 * @param {string} props.entityId - UUID of the SalesLead or SalesOpportunity
 * @param {Object} props.entity - SalesLead or SalesOpportunity object
 * @param {boolean} props.isOpen - Whether the panel is open
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onEntityRefresh - Refresh handler for the entity
 * @param {Function} props.onCallStatusChange - Called with (entityId, { callId, status }) whenever call status changes
 * @param {string} props.entityType - 'lead' or 'opportunity'
 * @param {string} props.initialOutreachType - Initial tab to show: 'call' or 'email' (default: 'call')
 * @param {Function} props.onNavigate - Navigation handler for prev/next
 * @param {number} props.currentIndex - Current index in the list
 * @param {number} props.totalEvents - Total number of items
 */
const RetellCallPanel = ({
  entityId,
  entity: entityProp,
  isOpen,
  onClose,
  onEntityRefresh,
  onCallStatusChange,
  entityType = 'opportunity',
  initialOutreachType = 'call',
  onNavigate = null,
  currentIndex = -1,
  totalEvents = 0
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Panel state
  const [alertState, setAlertState] = useState({ show: false, type: '', title: '', message: '' });
  const isAnkrAdmin = useIsAnkrAdmin();
  // Get live entity data from Redux or prop
  const leadsData = useSelector((state) => state.salesLeads?.leadsData || []);
  const opportunitiesData = useSelector((state) => state.salesOpportunities?.opportunitiesData || []);
  const leadsEditedRows = useSelector((state) => state.salesLeads?.editedRows || {});
  const opportunitiesEditedRows = useSelector((state) => state.salesOpportunities?.editedRows || {});

  // Merge base entity data with pending edits
  const liveEntity = useMemo(() => {
    // If entity passed as prop, use it
    if (entityProp) {
      return entityProp;
    }

    // Otherwise search in appropriate Redux data
    const baseEntity = entityType === 'lead'
      ? leadsData.find(e => e.id === entityId)
      : opportunitiesData.find(e => e.id === entityId);

    if (!baseEntity) return null;

    // Merge with pending edits
    const pendingEdits = entityType === 'lead'
      ? (leadsEditedRows[entityId] || {})
      : (opportunitiesEditedRows[entityId] || {});
    return { ...baseEntity, ...pendingEdits };
  }, [entityProp, leadsData, opportunitiesData, leadsEditedRows, opportunitiesEditedRows, entityId, entityType]);

  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);

  // Navigation state
  const [isNavigating, setIsNavigating] = useState(false);
  const navigateTimerRef = useRef(null);

  // Call state
  const [callId, setCallId] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Call viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState(null);

  // Call queue panel state
  const [queuePanelOpen, setQueuePanelOpen] = useState(false);

  // Call setup visibility state
  const [showCallSetup, setShowCallSetup] = useState(true);

  // Outreach type state - Initialize from prop
  const [outreachType, setOutreachType] = useState(initialOutreachType);

  // Update outreachType when initialOutreachType prop changes
  useEffect(() => {
    if (isOpen && initialOutreachType) {
      setOutreachType(initialOutreachType);
    }
  }, [isOpen, initialOutreachType]);

  // Handle tab switching with URL update
  const handleTabSwitch = useCallback((newOutreachType) => {
    setOutreachType(newOutreachType);

    // Update URL to reflect the current tab
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('panel', newOutreachType);

    // Preserve the entity ID if present
    if (entityId) {
      searchParams.set('id', entityId);
    }

    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  }, [entityId, location.pathname, location.search, navigate]);

  // Custom context fields
  const [phoneNumber, setPhoneNumber] = useState(''); // E.164 format for API calls
  const [phoneExtension, setPhoneExtension] = useState(''); // Extension for the phone number
  const [fromNumber, setFromNumber] = useState('');
  const [prospectName, setProspectName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');


  // Store outreach history from DB
  const [outreachHistory, setOutreachHistory] = useState([]);
  const [loadingOutreachHistory, setLoadingOutreachHistory] = useState(false);
  const [refreshingCallId, setRefreshingCallId] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);

  // Activity detail expansion (generic for all activity types: calls, emails, notes, etc.)
  const [expandedActivities, setExpandedActivities] = useState({});
  const [activityDetails, setActivityDetails] = useState({}); // Map: activityId -> full activity data
  const [loadingActivityDetails, setLoadingActivityDetails] = useState({});

  // Filter phone numbers based on selected agent
  const availablePhoneNumbers = useMemo(() => {
    if (!selectedAgentId || phoneNumbers.length === 0) {
      return phoneNumbers;
    }
    return phoneNumbers.filter(phone => {
      // Check outbound_agent_id (single agent)
      if (phone.outbound_agent_id === selectedAgentId) {
        return true;
      }
      // Check outbound_agents array
      if (phone.outbound_agents && Array.isArray(phone.outbound_agents)) {
        return phone.outbound_agents.some(agent => agent.agent_id === selectedAgentId);
      }
      // Check inbound_agent_id (single agent)
      if (phone.inbound_agent_id === selectedAgentId) {
        return true;
      }
      // Check inbound_agents array
      if (phone.inbound_agents && Array.isArray(phone.inbound_agents)) {
        return phone.inbound_agents.some(agent => agent.agent_id === selectedAgentId);
      }
      return false;
    });
  }, [selectedAgentId, phoneNumbers]);

  // Load outreach history from OutreachActivity table
  const loadOutreachHistory = useCallback(async () => {
    if (!entityId) return;

    try {
      setLoadingOutreachHistory(true);
      const response = await adminApi.getOutreachActivities({
        entity_id: entityId,
        entity_type: entityType
      });
      if (response.data) {
        setOutreachHistory(response.data);
      } else {
        setOutreachHistory([]);
      }
    } catch (err) {
      setOutreachHistory([]);
    } finally {
      setLoadingOutreachHistory(false);
    }
  }, [entityId, entityType]);

  // Auto-refresh outreach history while panel is open (only if enabled)
  useEffect(() => {
    if (isOpen && entityId && autoRefreshEnabled) {
      // Don't call immediately - the initial load useEffect already did
      const interval = setInterval(loadOutreachHistory, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityId, autoRefreshEnabled, loadOutreachHistory]);

  // Initialize form fields from entity data or last outreach activity
  // Store the initial outreach history in a ref to avoid re-initialization on polling updates
  const initialOutreachHistoryRef = useRef(null);

  useEffect(() => {
    if (liveEntity && isOpen) {
      // Reset snapshot when entity changes
      if (!initialOutreachHistoryRef.current ||
          (outreachHistory.length > 0 &&
           outreachHistory[0].object_id !== liveEntity.id)) {
        initialOutreachHistoryRef.current = null;
      }

      // Use initial outreach history snapshot to avoid re-initialization on polling updates
      const relevantHistory = initialOutreachHistoryRef.current || outreachHistory;
      const retellData = (relevantHistory && relevantHistory.length > 0)
        ? (relevantHistory[0].retell_data || {})
        : {};

      // Only override phone number if we have saved data from retellData (last used number)
      // Otherwise, PhoneNumberManager component will initialize from phone_numbers array via onChange callback
      if (retellData.to_number) {
        // Parse extension from to_number (format: "+1234567890;ext")
        const parts = retellData.to_number.split(';');
        setPhoneNumber(parts[0]);
        setPhoneExtension(parts.length > 1 ? parts[1] : '');
      }
      // Don't clear phone state here - PhoneNumberManager will set it via onChange

      // Single consolidated fallback chain - retell_data now stores these values from backend
      setProspectName(retellData.prospect_name || liveEntity.prospect_name || liveEntity.hiring_team?.[0]?.full_name || liveEntity.hiring_team_full_name || '');
      setCompanyName(retellData.company_name || liveEntity.company_object?.name || liveEntity.company || '');
      setAdditionalContext(retellData.additional_context || '');

      // Reset call state when switching entities
      setCallId(null);
      setCallStatus(null);
      setShowCallSetup(true); // Reset setup visibility when switching entities
      setAlertState({ show: false, type: '', title: '', message: '' });
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveEntity?.id, isOpen]); // Removed outreachHistory dependency to prevent form reset on polling

  // Store initial outreach history snapshot when it first loads
  useEffect(() => {
    if (outreachHistory && outreachHistory.length > 0 && !initialOutreachHistoryRef.current) {
      initialOutreachHistoryRef.current = outreachHistory;
    }
    // Reset snapshot when panel closes
    if (!isOpen) {
      initialOutreachHistoryRef.current = null;
    }
  }, [outreachHistory, isOpen]);

  // Reset phone number if current selection is not available for the selected agent
  useEffect(() => {
    // Auto-set phone number if only one is available
    if (availablePhoneNumbers.length === 1 && !callId) {
      setFromNumber(availablePhoneNumbers[0].phone_number);
    }
    // Clear fromNumber if currently selected number is no longer available
    else if (fromNumber && availablePhoneNumbers.length > 0) {
      const isAvailable = availablePhoneNumbers.some(phone => phone.phone_number === fromNumber);
      if (!isAvailable) {
        setFromNumber('');
      }
    }
  }, [selectedAgentId, availablePhoneNumbers, fromNumber, callId]);

  // Load available Retell AI agents
  const loadAgents = useCallback(async () => {
    try {
      setLoadingAgents(true);
      setAlertState({ show: false, type: '', title: '', message: '' });
      const response = await adminApi.getRetellAgents();

      if (response.data && response.data.success && response.data.data) {
        // Deduplicate agents by agent_id
        const uniqueAgents = [];
        const seenIds = new Set();
        for (const agent of response.data.data) {
          if (!seenIds.has(agent.agent_id)) {
            uniqueAgents.push(agent);
            seenIds.add(agent.agent_id);
          }
        }
        setAgents(uniqueAgents);
        // Auto-select first agent if available
        if (uniqueAgents.length > 0) {
          setSelectedAgentId(uniqueAgents[0].agent_id);
        }
      }
    } catch (err) {
      setAlertState({ show: true, type: 'danger', title: '', message: err.response?.data?.error || 'Failed to load agents' });
    } finally {
      setLoadingAgents(false);
    }
  }, []); // No dependencies - function doesn't depend on props or state

  // Load available Retell AI phone numbers
  const loadPhoneNumbers = useCallback(async () => {
    try {
      setLoadingPhoneNumbers(true);
      const response = await adminApi.getRetellPhoneNumbers();

      if (response.data && response.data.success && response.data.data) {
        setPhoneNumbers(response.data.data);
      }
    } catch (err) {
      // console.error('Error loading phone numbers:', err);
      // Don't set error state for phone numbers, as it's not critical
    } finally {
      setLoadingPhoneNumbers(false);
    }
  }, []); // No dependencies - function doesn't depend on props or state

  // Load agents, phone numbers, outreach history, and FULL entity data on mount
  useEffect(() => {
    if (isOpen && entityId) {
      loadAgents();
      loadPhoneNumbers();
      loadOutreachHistory();

      // Fetch full entity to ensure we have all fields including phone_numbers
      // List endpoint doesn't include phone_numbers, so we need detail endpoint
      if (onEntityRefresh) {
        onEntityRefresh(entityId);
      }
    }
  }, [isOpen, entityId, loadAgents, loadPhoneNumbers, loadOutreachHistory, onEntityRefresh]);

  // Poll for queue status from backend
  const pollQueueStatus = useCallback(async (queueId) => {
    try {
      const response = await adminApi.getCallQueueItem(queueId);
      if (response.data && response.data.success) {
        const queueData = response.data.call;

        // Determine actual call status - distinguish queue status from call status
        let actualCallStatus = queueData.status;
        const retellCallStatus = queueData.retell_response?.call_status;

        // If queue is "completed" and we have a retell_call_id, use the actual call status
        if (queueData.status === 'completed' && queueData.retell_call_id && retellCallStatus) {
          // Map Retell call statuses to display statuses
          if (retellCallStatus === 'not_connected' || retellCallStatus === 'error') {
            actualCallStatus = 'failed';
          } else if (retellCallStatus === 'ongoing' || retellCallStatus === 'registered') {
            actualCallStatus = 'in_progress';
          } else if (retellCallStatus === 'ended' || retellCallStatus === 'analyzed') {
            actualCallStatus = 'completed';
          } else {
            // Use retell status as-is for other cases
            actualCallStatus = retellCallStatus;
          }
        }

        // Update status display with queue information
        setCallStatus({
          call_status: actualCallStatus,
          queue_position: queueData.queue_position,
          queue_id: queueId,
          retell_call_id: queueData.retell_call_id,
          started_at: queueData.started_at,
          completed_at: queueData.completed_at,
          error_message: queueData.error_message,
          retell_response: queueData.retell_response,
          disconnection_reason: queueData.retell_response?.disconnection_reason
        });

        if (onCallStatusChange && entityId) {
          onCallStatusChange(entityId, { callId: queueId, status: queueData.status });
        }

        // Stop polling when call completes, fails, or is cancelled
        if (queueData.status === 'completed' || queueData.status === 'failed' || queueData.status === 'cancelled') {
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }

          // Refresh outreach history to show final status
          await loadOutreachHistory();

          // Show error alert if call failed (either queue failed or call didn't connect)
          if (actualCallStatus === 'failed') {
            // Prioritize Retell's detailed error message over generic error_message
            const errorMsg = queueData.retell_response?.message || queueData.error_message || 'Unknown error';
            setAlertState({ show: true, type: 'danger', title: 'Call Failed', message: errorMsg });
          }
        }
      }
    } catch (err) {
      // Handle queue item not found (deleted manually or expired)
      if (err.response?.status === 404) {
        // Stop polling - queue item no longer exists
        if (pollingInterval) {
          clearInterval(pollingInterval);
          setPollingInterval(null);
        }

        // Clear call status display
        setCallStatus(null);
        setCallId(null);

        // Refresh outreach history
        await loadOutreachHistory();
        setAlertState({ show: true, type: 'danger', title: '', message: 'Call removed from queue' });
      } else {
        setAlertState({ show: true, type: 'danger', title: '', message: `Error getting queue status: ${err.response?.data?.message || err.message}` });
      }
    }
  }, [pollingInterval, entityId, onCallStatusChange, loadOutreachHistory]);

  // Initiate call
  const handleInitiateCall = async () => {
    if (!phoneNumber) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Prospect phone number is required' });
      return;
    }
    if (!fromNumber) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Agent phone number is required.' });
      return;
    }
    if (!selectedAgentId) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Please select an agent' });
      return;
    }

    // Convert to E.164 and validate
    const toNumberE164 = toE164(phoneNumber);
    const fromNumberE164 = toE164(fromNumber);

    if (!isValidE164(toNumberE164)) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Invalid prospect phone number. Please enter a valid phone number.' });
      return;
    }
    if (!isValidE164(fromNumberE164)) {
      setAlertState({ show: true, type: 'danger', title: '', message: 'Invalid agent phone number. Please enter a valid phone number.' });
      return;
    }
    try {
      setLoading(true);
      setAlertState({ show: false, type: '', title: '', message: '' });

      // Build additional context with extension information if present
      let contextWithExtension = additionalContext;
      if (phoneExtension) {
        const extensionNote = `\n\nIMPORTANT: When the call connects, ask for extension ${phoneExtension}.`;
        contextWithExtension = additionalContext ? `${additionalContext}${extensionNote}` : extensionNote.trim();
      }

      // Append extension to phone number if present
      const toNumberWithExtension = phoneExtension ? `${toNumberE164};${phoneExtension}` : toNumberE164;

      const callData = {
        to_number: toNumberWithExtension,
        from_number: fromNumberE164,
        entity_type: entityType,
        entity_id: liveEntity?.id,
        prospect_name: prospectName,
        company_name: companyName,
        override_agent_id: selectedAgentId,
        agent_version: 'latest_published',
        additional_context: contextWithExtension
      };

      const response = await adminApi.initiateRetellCall(callData);

      if (response.data && response.data.success) {
        // Handle queue response (call is queued, not yet dialed)
        const queueId = response.data.queue_id;
        const queueData = response.data.call;

        // Set initial status as queued
        setCallId(queueId); // Use queue ID temporarily until call is dialed
        setCallStatus({
          call_status: queueData?.status || 'queued',
          queue_position: response.data.queue_position,
          estimated_dial_time: response.data.estimated_dial_time,
          queue_id: queueId
        });

        if (onCallStatusChange && entityId) {
          onCallStatusChange(entityId, {
            callId: queueId,
            status: 'queued',
            queue_position: response.data.queue_position
          });
        }

        // Just refresh outreach history to show the new entry
        try {
          await loadOutreachHistory();
        } catch (historyErr) {
          // console.error('Failed to refresh outreach history:', historyErr);
          // Don't show error to user, as call was successfully queued
        }

        // Show success message
        setAlertState({
          show: true,
          type: 'success',
          title: 'Call Queued',
          message: `Call to ${phoneNumber} added to queue (Position: ${response.data.queue_position})`
        });

        // Collapse the setup section after successful queue
        setShowCallSetup(false);

        // Update prospect name if it was filled in and is different from current value
        if (prospectName && prospectName !== (liveEntity.prospect_name || '')) {
          try {
            // Call the appropriate API based on entity type
            if (entityType === 'lead') {
              await adminApi.updateSalesLead(liveEntity.id, {
                prospect_name: prospectName
              });
            } else {
              await adminApi.updateSalesOpportunity(liveEntity.id, {
                prospect_name: prospectName
              });
            }
            // Refresh entity data to sync with LeadAndOpportunityTracker
            if (onEntityRefresh) {
              await onEntityRefresh(liveEntity.id);
            }
          } catch (nameErr) {
            // Don't show error to user, as this is a non-critical update
          }
        }

        // Auto-advance from the initial default stage to 'Contacted' after a call
        const isAtDefaultStage = liveEntity.contact_status_detail?.is_default;
        if (isAtDefaultStage && entityType === 'lead') {
          try {
            const statusesRes = await adminApi.getLeadOpportunityStatuses();
            // Only consider statuses that apply to this entity type
            const scopedStatuses = (statusesRes.data || []).filter(
              s => s.applies_to === entityType || s.applies_to === 'both'
            );
            const contactedStatus = scopedStatuses.find(s => s.slug === 'contacted');

            if (contactedStatus) {
              // Save status change immediately to database
              if (entityType === 'lead') {
                await adminApi.updateSalesLead(liveEntity.id, {
                  contact_status: contactedStatus.id
                });
              } else {
                await adminApi.updateSalesOpportunity(liveEntity.id, {
                  contact_status: contactedStatus.id
                });
              }
              // Refresh entity data
              if (onEntityRefresh) {
                await onEntityRefresh(liveEntity.id);
              }
            }
          } catch (statusErr) {
            setAlertState({ show: true, type: 'danger', title: '', message: `Call initiated but failed to update status: ${statusErr.response?.data?.message || statusErr.message}` });
          }
        }

        // Start polling queue status every 3 seconds to check when call is dialed
        const interval = setInterval(() => {
          pollQueueStatus(queueId);
        }, 3000);
        setPollingInterval(interval);

        // Initial queue status check
        pollQueueStatus(queueId);
      } else {
        // Parse error details for better user feedback
        const errorDetails = response.data?.details;
        let errorMessage = response.data?.error || 'Failed to initiate call';

        // Check if it's a phone number not found error
        if (errorDetails?.message && errorDetails.message.includes('not found from phone-number')) {
          errorMessage = `Agent is not allowed to use phone number ${fromNumber}. Try a different number or contact Admin (ERR 04325).`;
        } else if (errorDetails?.message) {
          errorMessage = `${errorMessage}: ${errorDetails.message}`;
        }

        setAlertState({ show: true, type: 'danger', title: '', message: errorMessage });
      }
    } catch (err) {
      // Parse error for better feedback
      let errorMessage = 'Failed to start call';
      const errorData = err.response?.data;

      if (errorData?.details?.message && errorData.details.message.includes('not found from phone-number')) {
        const phoneMatch = errorData.details.message.match(/Item ([+\d]+) not found/);
        const phoneNum = phoneMatch ? phoneMatch[1] : fromNumber;
        errorMessage = `Agent is not allowed to use phone number: ${phoneNum}. Try a different number or contact Admin.`;
      } else if (errorData?.details?.message && errorData.details.message.includes('not a valid number')) {
        const phoneMatch = errorData.details.message.match(/number provided: ([+\d]+)/);
        const invalidPhone = phoneMatch ? phoneMatch[1] : phoneNumber;
        errorMessage = `Invalid phone number: ${invalidPhone}. Check format and number and try again.`;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
        if (errorData.details?.message) {
          errorMessage += `: ${errorData.details.message}`;
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setAlertState({ show: true, type: 'danger', title: '', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setCallId(null);
      setCallStatus(null);
      setAlertState({ show: false, type: '', title: '', message: '' });
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [isOpen, pollingInterval]);

  if (!liveEntity) {
    return null;
  }

  const getStatusBadge = (status) => {
    const badges = {
      // Retell call statuses
      'registered': 'bg-secondary',
      'ongoing': 'bg-primary',
      'in_progress': 'bg-primary',
      'ended': 'bg-success',
      'analyzed': 'bg-success',
      'not_connected': 'bg-danger',
      'error': 'bg-danger',
      'canceled': 'bg-warning',
      // Queue statuses
      'pending': 'bg-secondary',
      'queued': 'bg-info',
      'scheduled': 'bg-info',
      'completed': 'bg-success',
      'failed': 'bg-danger',
      'cancelled': 'bg-warning'
    };
    return badges[status] || 'bg-secondary';
  };

  const handleViewCall = (callId) => {
    setSelectedCallId(callId);
    setViewerOpen(true);
  };

  // Toggle activity detail expansion and fetch full data if needed
  const handleToggleActivityDetail = async (activityId) => {
    const isExpanding = !expandedActivities[activityId];

    // Toggle expansion state
    setExpandedActivities(prev => ({
      ...prev,
      [activityId]: isExpanding
    }));

    // If expanding and we don't have full details yet, fetch them
    if (isExpanding && !activityDetails[activityId]) {
      try {
        setLoadingActivityDetails(prev => ({ ...prev, [activityId]: true }));
        const response = await adminApi.getOutreachActivity(activityId);
        setActivityDetails(prev => ({
          ...prev,
          [activityId]: response.data
        }));
      } catch (err) {
        setAlertState({
                show: true,
                type: 'warning',
                title: '',
                message: `Error occurred while fetching activity details. Try again later (${err.response?.data?.error})`
              });
      } finally {
        setLoadingActivityDetails(prev => ({ ...prev, [activityId]: false }));
      }
    }
  };

  const handleRefreshSingleItem = async (entry) => {
    const activityId = entry.id;
    const callId = entry.retell_data?.call_id;
    const queueId = entry.retell_data?.queue_id;
    const isQueuedCall = queueId && !callId;

    try {
      setRefreshingCallId(queueId || callId);
      setAlertState({ show: false, type: '', title: '', message: '' });

      if (isQueuedCall) {
        // Queued call: fetch queue status, update local state only
        try {
          const queueResponse = await adminApi.getCallQueueItem(queueId);
          const queueData = queueResponse.data?.call;

          // Determine actual call status - distinguish queue status from call status
          let actualCallStatus = queueData.status;
          const retellCallStatus = queueData.retell_response?.call_status;

          // If queue is "completed" and we have a retell_call_id, use the actual call status
          if (queueData.status === 'completed' && queueData.retell_call_id && retellCallStatus) {
            // Map Retell call statuses to display statuses
            if (retellCallStatus === 'not_connected' || retellCallStatus === 'error') {
              actualCallStatus = 'failed';
            } else if (retellCallStatus === 'ongoing' || retellCallStatus === 'registered') {
              actualCallStatus = 'in_progress';
            } else if (retellCallStatus === 'ended' || retellCallStatus === 'analyzed') {
              actualCallStatus = 'completed';
            } else {
              // Use retell status as-is for other cases
              actualCallStatus = retellCallStatus;
            }
          }

          setOutreachHistory(prev =>
            prev.map(item =>
              item.id === activityId
                ? {
                    ...item,
                    retell_data: {
                      ...item.retell_data,
                      call_id: queueData.retell_call_id || null,
                      call_status: actualCallStatus,
                      queue_position: queueData.queue_position,
                      error_message: queueData.error_message || null,
                      retell_response: queueData.retell_response || null,
                      disconnection_reason: queueData.retell_response?.disconnection_reason
                    }
                  }
                : item
            )
          );
        } catch (queueErr) {
          if (queueErr.response?.status === 404) {
            // Queue item gone - call was processed. Fetch full history to get updated data
            await loadOutreachHistory();
            setAlertState({ show: true, type: 'success', title: '', message: 'Call processed - history updated' });
            setTimeout(() => setAlertState({ show: false, type: '', title: '', message: '' }), 2000);
            return;
          }
          throw queueErr; // Re-throw other errors
        }
      } else if (callId) {
        // Completed call: fetch specific outreach activity
        const activityResponse = await adminApi.getOutreachActivity(activityId);
        setOutreachHistory(prev =>
          prev.map(item =>
            item.id === activityId ? activityResponse.data : item
          )
        );
      }
      setAlertState({ show: true, type: 'success', title: '', message: 'Item refreshed' });
      setTimeout(() => setAlertState({ show: false, type: '', title: '', message: '' }), 2000);
    } catch (err) {
      if (err.response?.status === 404) {
        // Item gone - trigger full refresh
        await loadOutreachHistory();
        setAlertState({ show: true, type: 'danger', title: '', message: 'Item no longer exists' });
      } else {
        setAlertState({ show: true, type: 'danger', title: '', message: err.response?.data?.error || 'Failed to refresh' });
      }
    } finally {
      setRefreshingCallId(null);
    }
  };

  const handleSavePhoneNumbers = async (updatedPhoneNumbers) => {
    try {
      // Call the appropriate API based on entity type
      if (entityType === 'lead') {
        await adminApi.updateSalesLead(liveEntity.id, { phone_numbers: updatedPhoneNumbers });
      } else {
        await adminApi.updateSalesOpportunity(liveEntity.id, { phone_numbers: updatedPhoneNumbers });
      }

      // Refresh entity data from DB to ensure consistency
      if (onEntityRefresh) {
        await onEntityRefresh(liveEntity.id);
      }

      setAlertState({ show: true, type: 'success', title: '', message: 'Phone numbers updated successfully' });
      // Auto-dismiss after 3 seconds
      setTimeout(() => {
        setAlertState({ show: false, type: '', title: '', message: '' });
      }, 3000);
    } catch (err) {
      setAlertState({ show: true, type: 'danger', title: '', message: err.response?.data?.message || 'Failed to update phone numbers' });
    }
  };

  // Handle navigation (prev/next)
  const handleNavigate = (direction) => {
    if (isNavigating) return;
    setIsNavigating(true);
    if (onNavigate) {
      onNavigate(direction);
    }
    // Re-enable after 400ms to let the remount settle
    clearTimeout(navigateTimerRef.current);
    navigateTimerRef.current = setTimeout(() => setIsNavigating(false), 400);
  };

  const canNavigatePrev = onNavigate && currentIndex > 0;
  const canNavigateNext = onNavigate && currentIndex >= 0 && currentIndex < totalEvents - 1;

  const renderTitle = () => (
    <div className="d-flex align-items-center justify-content-between w-100">
      AI Sales Rep Outreach
      {onNavigate && totalEvents > 0 && (
        <div className="d-flex align-items-center gap-2 ms-3">
          <span className="text-muted small">
            {currentIndex + 1} of {totalEvents}
          </span>
          <div className="btn-group btn-group-sm">
            <button
              className="btn btn-outline-secondary"
              onClick={() => handleNavigate('prev')}
              disabled={!canNavigatePrev || isNavigating}
              title="Previous"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => handleNavigate('next')}
              disabled={!canNavigateNext || isNavigating}
              title="Next"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
    <SidebarPanel
      isOpen={isOpen}
      onClose={onClose}
      title={renderTitle()}
      showCloseButton={true}
      style={{ width: '900px', maxWidth: '95vw' }}
    >
      <div className="p-2">
        {alertState.show && (
          <AlertMessage
            type={alertState.type}
            title={alertState.title}
            message={alertState.message}
            dismissible={true}
            onDismiss={() => setAlertState({ ...alertState, show: false })}
            className="mb-3"
          />
        )}

        {/* Outreach Type Toggle */}
        <ul className="nav nav-pills mb-3">
          <li className="nav-item">
            <button
              className={`nav-link ${outreachType === OUTREACH_TYPES.CALL ? 'active' : ''}`}
              onClick={() => handleTabSwitch(OUTREACH_TYPES.CALL)}
            >
              <i className="fas fa-phone me-2"></i>Voice Calls
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${outreachType === OUTREACH_TYPES.EMAIL ? 'active' : ''}`}
              onClick={() => handleTabSwitch(OUTREACH_TYPES.EMAIL)}
            >
              <i className="fas fa-envelope me-2"></i>Email Campaigns
            </button>
          </li>
        </ul>

      {/* Prospect Summary */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="card-title text-primary border-0 mb-3">Prospect Summary</h6>
          <div className="mb-2">
            <strong>Company:</strong>{' '}
            {liveEntity.company_object?.name || liveEntity.company || 'N/A'}
          </div>
          <div className="mb-2">
            <strong>Job Title:</strong>{' '}
            {liveEntity.url ? (
              <a href={liveEntity.url} target="_blank" rel="noopener noreferrer">
                {liveEntity.job_title || 'N/A'}
                <i className="fas fa-external-link-alt fa-sm text-primary ms-2"></i>
              </a>
            ) : ( liveEntity.job_title || 'N/A' )}
          </div>
          <div className="mb-2">
            <strong>Location:</strong> {liveEntity.location || liveEntity.short_location || 'N/A'}
          </div>
          {liveEntity.company_object?.industry && (<div className="mb-2">
            <strong>Industry:</strong> {liveEntity.company_object?.industry || 'N/A'}
          </div> )}
          {liveEntity.company_object?.employee_count && (
            <div className="mb-2">
              <strong>Employees:</strong> {liveEntity.company_object.employee_count} ({liveEntity.company_object.employee_count_range})
            </div>
          )}
          {liveEntity.company_object?.annual_revenue_usd_readable && (
            <div className="mb-2">
              <strong>Revenue:</strong> ${liveEntity.company_object.annual_revenue_usd_readable}
            </div>
          )}
          {liveEntity.company_object?.url && (
            <div className="mb-2">
              <strong>Website:</strong>{' '}
              <a href={liveEntity.company_object.url} target="_blank" rel="noopener noreferrer">
                {liveEntity.company_object.domain}
              </a>
            </div>
          )}
        </div>
      </div>
      
      {/* Call Setup - Only show for CALL outreach type */}
      {outreachType === OUTREACH_TYPES.CALL && (
      <div className="card">
        <div
          className="card-header bg-white d-flex justify-content-between align-items-center border-0"
          style={{ cursor: 'pointer', borderRadius:'8px' }}
          onClick={() => { setShowCallSetup(!showCallSetup); if (!showCallSetup) { setCallId(null); setCallStatus(null); } }}
        >
          <h6 className="card-title text-primary border-0 mb-0">Setup Call</h6>
          <i className={`fas fa-chevron-${showCallSetup ? 'up' : 'down'} text-muted`}></i>
        </div>
        {showCallSetup && (
        <div className="card-body p-3 gap-3">
        <div className="row mb-3">
          <div className="col-6 mb-2">
          <label className="form-label fw-semibold mb-1">Select AI Agent</label>
          {loadingAgents ? (
            <div className="text-center py-2">
              <div className="spinner-border spinner-border-sm me-2" role="status"></div>
              <small>Loading agents...</small>
            </div>
          ) : agents.length === 0 ? (
            <div className="alert alert-warning py-2 mb-0">
              <small><i className="fas fa-exclamation-circle me-2"></i>No agents found</small>
            </div>
          ) : (
            <select
              className="form-select"
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              disabled={!!callId}
            >
              {agents.map((agent) => (
                <option key={agent.agent_id} value={agent.agent_id}>
                  {agent.agent_name || agent.agent_id}
                </option>
              ))}
            </select>
          )}
        </div>
          <div className="col-6">
            <label className="form-label fw-semibold mb-1">Agent&apos;s Phone Number *</label>
            {loadingPhoneNumbers ? (
              <div className="text-center py-2">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                <small>Loading phone numbers...</small>
              </div>
            ) : availablePhoneNumbers.length === 0 ? (
              <div>
                <input
                  type="tel"
                  className="form-control"
                  value={fromNumber}
                  onChange={(e) => setFromNumber(e.target.value)}
                  placeholder="+1xxx-xxx-xxxx"
                  disabled={!!callId}
                  title="No phone numbers available for this agent. Enter manually."
                />
                {selectedAgentId && phoneNumbers.length > 0 && (
                  <small className="text-muted d-block mt-1">
                    <i className="fas fa-info-circle me-1"></i>
                    No phone numbers configured for this agent
                  </small>
                )}
              </div>
            ) : availablePhoneNumbers.length === 1 ? (
              <div>
                <input
                  type="text"
                  className="form-control"
                  value={availablePhoneNumbers[0].phone_number_pretty || availablePhoneNumbers[0].phone_number}
                  disabled
                  title="Only phone number available for this agent"
                />
                {availablePhoneNumbers[0].nickname && (
                  <small className="text-muted d-block mt-1">
                    <i className="fas fa-info-circle me-1"></i>
                    {availablePhoneNumbers[0].nickname}
                  </small>
                )}
              </div>
            ) : (
              <select
                className="form-select"
                value={fromNumber}
                onChange={(e) => setFromNumber(e.target.value)}
                disabled={!!callId}
                title="Select from available phone numbers for this agent"
              >
                <option value="">Select a phone number...</option>
                {availablePhoneNumbers.map((phone) => (
                  <option key={phone.phone_number} value={phone.phone_number}>
                    {phone.phone_number_pretty || phone.phone_number} {phone.nickname ? `- ${phone.nickname}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="mb-2">
          <div className="row g-2">
            <div className="col-6 mb-2">
              <label className="form-label fw-semibold mb-1">Prospect Name</label>
              <input
                type="text"
                className="form-control"
                value={prospectName}
                onChange={(e) => setProspectName(e.target.value)}
                placeholder="Optional"
                disabled={!!callId}
              />
            </div>
            <div className="col-6">
              <label className="form-label fw-semibold mb-1">Prospect&apos;s Phone Number *</label>
              <PhoneNumberManager
                key={liveEntity?.id}
                phoneNumbers={liveEntity?.phone_numbers || []}
                onSave={handleSavePhoneNumbers}
                onChange={(e164Phone, extension) => {
                  setPhoneNumber(e164Phone);
                  setPhoneExtension(extension || '');
                }}
                disabled={!!callId}
              />
            </div>
          </div>
        </div>

        {/* Call Context */}
        <div className="mb-2">
          <div className="mb-2">
            <label className="form-label fw-semibold mb-1">Company Name to Use During Call</label>
            <input
              type="text"
              className="form-control"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder=""
              disabled={!!callId}
            />
          </div>
          <div>
            <label className="form-label fw-semibold mb-1">Any Context for AI Agent</label>
            <textarea
              className="form-control"
              rows="2"
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Prior conversation, did someone refer us, anything that can give your agent an edge..."
              disabled={!!callId}
            />
          </div>

          </div>
          {/* Action Buttons */}
          <div className="d-flex gap-2">
            <button className="btn btn-primary flex-grow-1" onClick={handleInitiateCall}
              disabled={loading || !fromNumber || !phoneNumber || !selectedAgentId} >
              {loading ? (
                <> <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Initiating... </>
              ) : ( <> <i className="fas fa-rocket me-2"></i> Start Call </> )}
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => setQueuePanelOpen(true)}
              title="View Call Queue"
            >
              <i className="fas fa-list-ul"></i>
            </button>
          </div>
        </div>
        )}
      </div>
      )}

      {/* Email Setup - Only show for EMAIL outreach type */}
      {outreachType === OUTREACH_TYPES.EMAIL && (
        <div className="card p-3">
          <CommunicationComposer
            entityType={entityType}
            entityId={entityId}
            entity={liveEntity}
            onCommunicationSent={(channel, record, response) => {
              // Refresh entity to show updated activity
              if (onEntityRefresh) {
                onEntityRefresh(entityId);
              }
              // Refresh outreach activity history to show new communication
              loadOutreachHistory();

              // Show success message
              const channelLabel = channel === 'sms' ? 'SMS' : channel === 'email' ? 'Email' : 'Message';
              setAlertState({
                show: true,
                type: 'success',
                title: '',
                message: `${channelLabel} sent to ${record.to}${response.data?.tracking_id ? ' (tracking enabled)' : ''}`
              });
            }}
            onCancel={() => {
              // User cancelled - do nothing
            }}
            compact={false}
            isAnkrAdmin={isAnkrAdmin}
            deferSend={false}
          />
        </div>
      )}

        {/* Call Status - Only show for CALL outreach type */}
        {outreachType === OUTREACH_TYPES.CALL && (
          <div className="card card-body mb-2">
            <h6 className="card-title text-primary border-0 mb-3">Live Call Status</h6>
              <div className="card-body p-2">
                {callStatus ? (
                <>
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                    <span className={`badge ${getStatusBadge(callStatus.call_status)}`}>
                      {callStatus.call_status || 'Unknown'}
                    </span>
                    {callStatus.queue_id && (
                      <code className="small" title="Queue ID">{callStatus.queue_id}</code>
                    )}
                    {callStatus.retell_call_id && (
                      <code className="small" title="Retell Call ID">{callStatus.retell_call_id}</code>
                    )}
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => setQueuePanelOpen(true)}
                      title="View Call Queue"
                    >
                      <i className="fas fa-list-ul"></i>
                    </button>
                  </div>

                  {/* Queue-specific info */}
                  {callStatus.queue_position && (
                    <div className="small text-muted mb-1">
                      <i className="fas fa-list-ol me-1"></i>
                      Queue Position: {callStatus.queue_position}
                    </div>
                  )}
                  {callStatus.estimated_dial_time && (
                    <div className="small text-muted mb-1">
                      <i className="fas fa-clock me-1"></i>
                      Estimated Dial: {new Date(callStatus.estimated_dial_time).toLocaleTimeString()}
                    </div>
                  )}
                  {(callStatus.retell_response?.message || callStatus.error_message || callStatus.disconnection_reason) && (
                    <div className="alert alert-danger py-2 mb-2">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-exclamation-circle me-2 mt-1"></i>
                        <div>
                          <strong>
                            {callStatus.call_status === 'not_connected' ? 'Call Not Connected:' : 'Call Failed:'}
                          </strong>
                          <div className="mt-1">
                            {callStatus.retell_response?.message || callStatus.error_message ||
                             (callStatus.disconnection_reason && `Disconnected: ${callStatus.disconnection_reason}`)}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Retell call timestamps */}
                  {(callStatus.start_timestamp || callStatus.end_timestamp) && (
                    <div className="small text-muted">
                      {callStatus.start_timestamp && (
                        <span className="me-2">Started: {new Date(callStatus.start_timestamp * 1000).toLocaleString()}</span>
                      )}
                      {callStatus.end_timestamp && (
                        <span>Ended: {new Date(callStatus.end_timestamp * 1000).toLocaleString()}</span>
                      )}
                    </div>
                  )}
                  {callStatus.call_analysis && (
                    <div className="mt-1">
                      <small className="fw-semibold">Analysis:</small>
                      <pre className="bg-light p-1 mt-1 mb-0" style={{ fontSize: '0.75rem', maxHeight: '120px', overflow: 'auto' }}>
                        {JSON.stringify(callStatus.call_analysis, null, 2)}
                      </pre>
                    </div>
                  )}
                </>
                ) : (
                  <div className="text-center text-muted py-1">
                    <p className="mb-0"><i className="fas fa-phone-slash mb-2"></i> No active call</p>
                    <button
                      className="btn btn-sm btn-outline-primary mt-2"
                      onClick={() => setQueuePanelOpen(true)}
                    >
                      <i className="fas fa-list-ul me-1"></i>View Call Queue
                    </button>
                  </div>
                )}
              </div>
          </div>
        )}

        {/* Outreach History */}
        <div className="card card-body p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="card-title text-primary mb-0 border-0">Outreach History</h6>
            <div className="d-flex gap-2">
              <button
                className={`btn btn-sm ${autoRefreshEnabled ? 'btn-outline-success' : 'btn-outline-secondary'} py-0 px-2`}
                onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                title={autoRefreshEnabled ? "Auto-refresh enabled (every 5s)" : "Enable auto-refresh"}
              >
                <i className={`fas fa-${autoRefreshEnabled ? 'pause' : 'play'} me-1`}></i>
                {autoRefreshEnabled ? 'Auto-refresh On' : 'Auto-refresh Off'}
              </button>
              <button
                className="btn btn-sm btn-outline-primary py-0 px-2"
                onClick={loadOutreachHistory}
                disabled={loadingOutreachHistory}
                title="Refresh outreach history from database"
              >
                {loadingOutreachHistory ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="fas fa-sync-alt"></i>
                )}
              </button>
            </div>
          </div>
          <div className="card">
            <div className="card-body p-2" style={{ maxHeight: '400px', overflow: 'auto' }}>
              {loadingOutreachHistory ? (
                <div className="text-center py-3" style={{ height: '400px'}} >
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Loading outreach history...
                </div>
              ) : (!outreachHistory || outreachHistory.length === 0) ? (
                <div className="text-muted text-center py-2">
                  <i className="fas fa-history me-2"></i>No outreach history
                </div>
              ) : (
                (() => {
                  // Group email opens with their sent emails
                  const processedEntries = [];
                  const emailOpensByTrackingId = new Map();

                  // First pass: collect all email opens by tracking_id
                  outreachHistory.forEach(entry => {
                    if (entry.action === 'email_opened') {
                      const trackingId = entry.retell_data?.tracking_id;
                      if (trackingId) {
                        if (!emailOpensByTrackingId.has(trackingId)) {
                          emailOpensByTrackingId.set(trackingId, []);
                        }
                        emailOpensByTrackingId.get(trackingId).push(entry);
                      }
                    }
                  });

                  // Second pass: build entries, attaching opens to sent emails
                  outreachHistory.forEach(entry => {
                    if (entry.action === 'email_opened') {
                      // Skip email_opened entries - they'll be nested under sent emails
                      return;
                    }

                    // Add the entry
                    const processedEntry = { ...entry };

                    // If this is an email_sent, attach any opens
                    if (entry.action === 'email_sent') {
                      const trackingId = entry.retell_data?.tracking_id;
                      if (trackingId && emailOpensByTrackingId.has(trackingId)) {
                        processedEntry.emailOpens = emailOpensByTrackingId.get(trackingId);
                      }
                    }

                    processedEntries.push(processedEntry);
                  });

                  return processedEntries.map((entry, index) => {
                    const activityType = entry.activity_type?.name || 'Unknown';
                    const activityDate = entry.activity_date;
                    const callId = entry.retell_data?.call_id || entry.retell_data?.queue_id;
                    const isQueueId = !entry.retell_data?.call_id && entry.retell_data?.queue_id;
                    const callStatus = entry.retell_data?.call_status;
                    const toNumber = entry.retell_data?.to_number;
                    const queuePosition = entry.retell_data?.queue_position;
                    const isLast = index === processedEntries.length - 1;
                    const emailOpens = entry.emailOpens || [];

                    // Determine if still actively in queue (not processed/completed/failed/cancelled)
                    const activeQueueStatuses = ['pending', 'queued', 'scheduled', 'in_progress'];
                    const isActivelyQueued = isQueueId && callStatus && activeQueueStatuses.includes(callStatus);
                    const hasEmailBody = entry.retell_data?.has_email_body;
                    const isExpanded = expandedActivities[entry.id];
                    const fullDetails = activityDetails[entry.id];
                    const isLoadingDetails = loadingActivityDetails[entry.id];

                    return (
                      <div
                        key={entry.id || index}
                        className={`pb-1 mb-1 ${!isLast ? 'border-bottom' : ''}`}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <span className="badge badge-sm bg-info me-1">{activityType.toUpperCase()}</span>
                            {callStatus && (
                              <span className={`badge badge-sm ${getStatusBadge(callStatus)}`}>
                                {callStatus}
                              </span>
                            )}
                            {emailOpens.length > 0 && (
                              <span className="badge badge-sm bg-success ms-1">
                                <i className="fas fa-envelope-open me-1"></i>Opened {emailOpens.length}x
                              </span>
                            )}
                          </div>
                          <p className="text-muted mb-0 small">
                            {new Date(activityDate).toLocaleDateString()} {new Date(activityDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div>
                          {toNumber && <div className="text-muted small">{toNumber}</div>}
                          {queuePosition && <div className="text-muted small">Queue Position: {queuePosition}</div>}
                          {entry.notes && <div className="text-muted small">{entry.notes}</div>}
                          {/* Show email subject if available */}
                          {entry.action === 'email_sent' && entry.retell_data?.subject && (
                            <div className="text-muted small">
                              <strong>Subject:</strong> {entry.retell_data.subject}
                            </div>
                          )}
                          {entry.action === 'email_sent' && entry.retell_data?.recipient && (
                            <div className="text-muted small">
                              <strong>To:</strong> {entry.retell_data.recipient}
                            </div>
                          )}

                          {/* Show nested email opens */}
                          {emailOpens.length > 0 && (
                            <div className="ms-3 mt-1 small text-success">
                              {emailOpens.map((openEntry, openIndex) => (
                                <div key={openEntry.id || openIndex} className="d-flex align-items-center gap-1">
                                  <i className="fas fa-check-circle"></i>
                                  <span>Opened {new Date(openEntry.activity_date).toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* View Details button for emails */}
                          {hasEmailBody && (
                            <div className="mt-1">
                              <button
                                className="btn btn-sm btn-outline-secondary py-0 px-1"
                                onClick={() => handleToggleActivityDetail(entry.id)}
                                disabled={isLoadingDetails}
                                title={isExpanded ? "Hide email body" : "View email body"}
                              >
                                {isLoadingDetails ? (
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                  <>
                                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} me-1`}></i>
                                    {isExpanded ? 'Hide' : 'View'} Email
                                  </>
                                )}
                              </button>
                            </div>
                          )}

                          {/* Expanded email body */}
                          {isExpanded && fullDetails && fullDetails.retell_data && (
                            <div className="mt-2 p-2 border rounded bg-light">
                              {/* Tracking info */}
                              {fullDetails.retell_data.tracking_id && (
                                <div className="mb-2 p-2 bg-white border rounded">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <small className="text-muted">
                                        <i className="fas fa-satellite-dish me-1"></i>
                                        Tracking {fullDetails.retell_data.email_opened ? 'Active' : 'Enabled'}
                                      </small>
                                      {fullDetails.retell_data.email_opened && fullDetails.retell_data.opened_at && (
                                        <small className="text-success ms-2">
                                          <i className="fas fa-check-circle me-1"></i>
                                          Opened {new Date(fullDetails.retell_data.opened_at).toLocaleString()}
                                        </small>
                                      )}
                                    </div>
                                    <button
                                      className="btn btn-sm btn-outline-secondary border-0 py-0 px-1"
                                      onClick={() => {
                                        const trackingUrl = `${window.location.origin}/api/admin/sales/track/email/${fullDetails.retell_data.tracking_id}`;
                                        navigator.clipboard.writeText(trackingUrl);
                                        setAlertState({ show: true, type: 'success', title: '', message: 'Tracking URL copied!' });
                                      }}
                                      title="Copy tracking pixel URL"
                                    >
                                      <i className="fas fa-link"></i>
                                    </button>
                                  </div>
                                </div>
                              )}

                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <strong className="small">Email Body:</strong>
                                <button
                                  className="btn btn-sm btn-outline-primary border-0 py-0 px-1"
                                  onClick={() => {
                                    const bodyText = fullDetails.retell_data.html_body || fullDetails.retell_data.text_body || '';
                                    navigator.clipboard.writeText(bodyText);
                                    setAlertState({ show: true, type: 'success', title: '', message: 'Email copied to clipboard!' });
                                  }}
                                  title="Copy to clipboard"
                                >
                                  <i className="fas fa-copy"></i>
                                </button>
                              </div>
                              <div
                                className="email-body-preview p-2"
                                style={{
                                  maxHeight: '300px',
                                  overflowY: 'auto',
                                  fontSize: '0.85rem',
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6'
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeEmailHtml(fullDetails.retell_data.html_body) || `<pre>${fullDetails.retell_data.text_body || 'No body available'}</pre>`
                                }}
                              />
                            </div>
                          )}

                          {callId && (
                            <div className="d-flex justify-content-between align-items-center mt-1">
                              <code className="small" title={isQueueId ? "Queue ID" : "Call ID"}>
                                {isActivelyQueued && <i className="fas fa-hourglass-half me-1 text-muted"></i>}
                                {callId}
                              </code>
                              <div className="d-flex gap-2">
                                {isActivelyQueued && (
                                  <span className="text-secondary me-2">
                                    <i className="fas fa-clock me-1"></i>
                                    {callStatus === 'in_progress' ? 'Processing' :
                                     callStatus === 'scheduled' ? 'Scheduled' :
                                     queuePosition ? `Queue #${queuePosition}` : 'In Queue'}
                                  </span>
                                )}
                                <button
                                  className="btn btn-outline-secondary py-0 px-1"
                                  onClick={() => handleRefreshSingleItem(entry)}
                                  disabled={refreshingCallId === callId}
                                  title="Refresh call status"
                                >
                                  {refreshingCallId === callId ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                  ) : (
                                    <i className="fas fa-sync-alt"></i>
                                  )}
                                </button>
                                {!isQueueId && (
                                  <button
                                    className="btn btn-outline-primary py-0 px-1"
                                    onClick={() => handleViewCall(callId)}
                                    title="View call details"
                                  >
                                    <i className="fas fa-eye me-1"></i>View
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()
              )}
            </div>
          </div>
        </div>

        {/* Lead Tracking Section */}
        <LeadAndOpportunityTracker
          entity={liveEntity}
          onUpdate={onEntityRefresh}
          entityType={entityType}
        />
      </div>
    </SidebarPanel>

      {/* Call Viewer Modal */}
      <RetellCallViewer
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        callId={selectedCallId}
      />

      {/* Call Queue Panel */}
      <CallQueuePanel
        isOpen={queuePanelOpen}
        onClose={() => setQueuePanelOpen(false)}
      />
    </>
  );
};

export default RetellCallPanel;
